import * as React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from './AuthProvider.jsx'
import { Button } from '@/components/ui/button.jsx'
import { Field } from '@/components/ui/field.jsx'
import { Alert } from '@/components/ui/feedback.jsx'

/**
 * "Forgot password?" (Phase 2, item 18a — D28b).
 *
 * Same unauthenticated, pre-tenant posture as LoginPage: no token exists yet,
 * so this talks to Supabase Auth directly rather than the MBOS API, and it
 * renders in the default palette because whose workspace this is is not yet
 * known.
 *
 * ── ALWAYS THE SAME CONFIRMATION, REGARDLESS OF OUTCOME ─────────────────────
 * Supabase's resetPasswordForEmail does not report whether the address has an
 * account, and this screen does not either — the alternative would let
 * anyone probe which emails are registered to a workspace, one submission at
 * a time.
 */
export function ForgotPasswordPage () {
  const { requestPasswordReset } = useAuth()
  const [email, setEmail] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [sent, setSent] = React.useState(false)
  const [error, setError] = React.useState(null)

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await requestPasswordReset(email.trim())
      setSent(true)
    } catch (err) {
      // Deliberately generic — see the module note above.
      setError(err?.message ?? 'Could not send the reset email. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">MBOS</h1>
          <p className="mt-1.5 text-sm text-ink-muted">Reset your password</p>
        </div>

        {sent
          ? (
            <div className="space-y-5 rounded-lg border border-line bg-surface p-6 shadow-card">
              <Alert tone="success" title="Check your email">
                If an account exists for {email.trim()}, a password reset link is on its way.
              </Alert>
              <Link to="/login" className="block text-center text-sm text-brand hover:underline">
                Back to sign in
              </Link>
            </div>
            )
          : (
            <form
              onSubmit={handleSubmit}
              className="space-y-5 rounded-lg border border-line bg-surface p-6 shadow-card"
              noValidate
            >
              {error && <Alert tone="danger">{error}</Alert>}

              <p className="text-sm/relaxed text-ink-muted">
                Enter the email you sign in with. We will send a link to set a new password.
              </p>

              <Field
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                required
                disabled={submitting}
              />

              <Button type="submit" className="w-full" loading={submitting} disabled={!email.trim()}>
                {submitting ? 'Sending' : 'Send reset link'}
              </Button>

              <Link to="/login" className="block text-center text-sm text-ink-muted hover:underline">
                Back to sign in
              </Link>
            </form>
            )}
      </div>
    </div>
  )
}
